import { useState } from 'react';
import api from 'api';
import { actions } from 'store';
import { useDispatch, useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { Alert, ModalButtonWithForm } from 'components/common';
import { useBoxDispatch } from 'components/common/box';
import { FormGroup, RadioGroup, Text } from 'components/common/form';
import { UsersListSelectedUsers } from '.';

import type { UpdateUserObject } from './Users';
import type { SelectedUser } from './UsersListSelectedUsers';

interface Props {
  handleSuccess: (updateUser: UpdateUserObject) => Promise<void>;
}

const UsersDeleteInBatch = ({ handleSuccess }: Props) => {
  const boxDispatch = useBoxDispatch();

  const { dispatch } = useDispatch();

  const {
    messages,
    serverConfig: { accountAnonymiseOnDeletion, requireReasonWhenDeletingData },
    user: loggedInUser
  } = useReduxState();

  const [users, setUsers] = useState<SelectedUser[]>([]);
  const [visible, setVisible] = useState(false);

  const submit = async (values: {
    anonymise: boolean;
    deletionReason?: string;
  }) => {
    const params = new URLSearchParams({
      anonymise: String(values.anonymise),
      ...(values.deletionReason && { deletionReason: values.deletionReason })
    });

    const response = await api.post(
      `/admin/users-delete-in-batch?${params.toString()}`,
      { ids: users.map((user) => user.id) }
    );
    if (!response.data || !response.data.success) return false;

    await handleSuccess({});

    boxDispatch({ type: 'resetSelection' });
    dispatch(
      actions.addNotice({
        text: `${response.data.numberOfDeletedUsers} ${messages.usersDeleteUsersSuccess}`,
        type: 'success'
      })
    );

    if (response.data?.numberOfDeleteRequests > 0) {
      dispatch(
        actions.addNotice({
          text: getMessageWithPlaceholders({
            message: messages.usersDeleteUserMultipleDeleteRequest,
            placeholders: {
              numberOfUsers: response.data.numberOfDeleteRequests
            }
          }),
          type: 'warning'
        })
      );
    }
  };

  return (
    <ModalButtonWithForm
      buttonCategory="danger"
      buttonIcon="trash-alt"
      buttonIsDropdown={true}
      buttonStyle={{ whiteSpace: 'nowrap' }}
      buttonText={messages.usersDeleteUserMultiple}
      initialValues={{ anonymise: accountAnonymiseOnDeletion }}
      modalActionText={messages.commonDelete}
      modalTitle={messages.usersDeleteUserMultiple}
      modalWidth="wide"
      onHide={() => setVisible(false)}
      onShow={() => setVisible(true)}
      onSubmit={submit}
    >
      {() => (
        <>
          {!accountAnonymiseOnDeletion ? (
            <Alert
              style={{ marginBottom: 10 }}
              text={messages.usersDeleteUserInfo}
              type="warning-light"
            />
          ) : null}

          {visible ? (
            <UsersListSelectedUsers
              allowGuestUsers={true}
              includeAdmins={loggedInUser?.roles.includes('superadmin')}
              onLoadedUsers={(users) => setUsers(users)}
            />
          ) : null}

          {requireReasonWhenDeletingData ? (
            <FormGroup
              htmlFor="deletionReason-batch"
              label={messages.commonDeletionReason}
            >
              <Text
                field="deletionReason"
                id="deletionReason-batch"
                maxLength={255}
                autocomplete="off"
                placeholder={messages.commonInputDeletionReason}
                required={true}
              />
            </FormGroup>
          ) : null}

          {accountAnonymiseOnDeletion ? (
            <FormGroup label={messages.usersDeleteUserMultipleConfirm}>
              <RadioGroup
                field="anonymise"
                id={`anonymise-in-batch`}
                labelStyle={{ fontWeight: 600 }}
                options={[
                  {
                    id: 'anonymise',
                    label: messages.usersAnonymise,
                    labelAdditionalContent: messages.usersDeleteUserAnonymise,
                    value: true
                  },
                  {
                    id: 'delete',
                    label: messages.usersDeleteUser,
                    labelAdditionalContent: messages.usersDeleteUserInfo,
                    value: false
                  }
                ]}
                style={{ marginTop: 5 }}
              />
            </FormGroup>
          ) : null}
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default UsersDeleteInBatch;
